import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BusinessHoursEditor from './BusinessHoursEditor';

const DEFAULT_CONFIG = {
  durationMinutes: 60,
  minBufferMinutes: 60,
  prepTimeMinutes: 0,
  maxMeetingsPerDay: null,
  maxMeetingsPerWeek: null,
  bookingWindowDays: 14,
  availabilityHours: {
    monday:    { enabled: true,  start: '09:00', end: '18:00' },
    tuesday:   { enabled: true,  start: '09:00', end: '18:00' },
    wednesday: { enabled: true,  start: '09:00', end: '18:00' },
    thursday:  { enabled: true,  start: '09:00', end: '18:00' },
    friday:    { enabled: true,  start: '09:00', end: '18:00' },
    saturday:  { enabled: false, start: '09:00', end: '18:00' },
    sunday:    { enabled: false, start: '09:00', end: '18:00' },
  },
  blockedDates: [],
  blockedRanges: [],
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];
const BUFFER_OPTIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hora' },
  { value: 120, label: '2 horas' },
  { value: 240, label: '4 horas' },
  { value: 480, label: '8 horas' },
  { value: 1440, label: '24 horas' },
  { value: 2880, label: '48 horas' },
];
const PREP_OPTIONS = [
  { value: 0, label: 'Sin pausa' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '60 min' },
];
const WINDOW_OPTIONS = [7, 14, 30, 60, 90];

export default function AvailabilityConfig({ schedulingConfig, onChange }) {
  const { t } = useTranslation('bots');
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG, ...schedulingConfig });
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newRangeStart, setNewRangeStart] = useState('');
  const [newRangeEnd, setNewRangeEnd] = useState('');
  const [newRangeLabel, setNewRangeLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (schedulingConfig) {
      setConfig({ ...DEFAULT_CONFIG, ...schedulingConfig });
    }
  }, [schedulingConfig]);

  const update = (field, value) => {
    const next = { ...config, [field]: value };
    setConfig(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onChange({ schedulingConfig: config });
      alert(t('integrations.calendar.availability.savedSuccess'));
    } catch (error) {
      console.error('Error saving availability config:', error);
      alert(t('integrations.calendar.availability.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const addBlockedDate = () => {
    if (!newBlockedDate || config.blockedDates.includes(newBlockedDate)) return;
    update('blockedDates', [...config.blockedDates, newBlockedDate].sort());
    setNewBlockedDate('');
  };

  const removeBlockedDate = (date) => {
    update('blockedDates', config.blockedDates.filter(d => d !== date));
  };

  const addBlockedRange = () => {
    if (!newRangeStart || !newRangeEnd || newRangeStart > newRangeEnd) return;
    const range = { start: newRangeStart, end: newRangeEnd };
    if (newRangeLabel) range.label = newRangeLabel;
    update('blockedRanges', [...config.blockedRanges, range]);
    setNewRangeStart('');
    setNewRangeEnd('');
    setNewRangeLabel('');
  };

  const removeBlockedRange = (idx) => {
    update('blockedRanges', config.blockedRanges.filter((_, i) => i !== idx));
  };

  return (
    <div className="availability-config">
      <div className="avail-header">
        <h4>{t('integrations.calendar.availability.title')}</h4>
        <p className="avail-desc">{t('integrations.calendar.availability.description')}</p>
        {/* El espejo de la frase de Conocimiento. Los dos horarios comparten editor y
            forma; sólo el texto los distingue. Ver `frasePlazoDeContacto.js`. */}
        <p className="avail-desc" style={{ fontSize: '13px', color: '#4a5568' }}>
          {t('integrations.calendar.availability.hint')}
        </p>
      </div>

      {/* Availability Hours */}
      <div className="avail-section">
        <h5>{t('integrations.calendar.availability.hoursTitle')}</h5>
        <BusinessHoursEditor
          value={config.availabilityHours}
          onChange={(hours) => update('availabilityHours', hours)}
        />
      </div>

      {/* Slot Configuration */}
      <div className="avail-section">
        <h5>{t('integrations.calendar.availability.slotsTitle')}</h5>
        <div className="avail-row">
          <div className="avail-field">
            <label>{t('integrations.calendar.availability.duration')}</label>
            <select value={config.durationMinutes} onChange={e => update('durationMinutes', Number(e.target.value))}>
              {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <div className="avail-field">
            <label>{t('integrations.calendar.availability.minBuffer')}</label>
            <select value={config.minBufferMinutes} onChange={e => update('minBufferMinutes', Number(e.target.value))}>
              {BUFFER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="avail-field">
            <label>{t('integrations.calendar.availability.prepTime')}</label>
            <select value={config.prepTimeMinutes} onChange={e => update('prepTimeMinutes', Number(e.target.value))}>
              {PREP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Limits */}
      <div className="avail-section">
        <h5>{t('integrations.calendar.availability.limitsTitle')}</h5>
        <div className="avail-row">
          <div className="avail-field">
            <label>{t('integrations.calendar.availability.maxPerDay')}</label>
            <input
              type="number"
              min="1"
              max="50"
              placeholder={t('integrations.calendar.availability.maxPerDayPlaceholder')}
              value={config.maxMeetingsPerDay ?? ''}
              onChange={e => update('maxMeetingsPerDay', e.target.value ? Number(e.target.value) : null)}
            />
          </div>
          <div className="avail-field">
            <label>{t('integrations.calendar.availability.bookingWindow')}</label>
            <select value={config.bookingWindowDays} onChange={e => update('bookingWindowDays', Number(e.target.value))}>
              {WINDOW_OPTIONS.map(d => <option key={d} value={d}>{d} {t('integrations.calendar.availability.bookingWindowSuffix')}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Blocked Dates */}
      <div className="avail-section">
        <h5>{t('integrations.calendar.availability.blockedTitle')}</h5>

        <div className="avail-subsection">
          <label>{t('integrations.calendar.availability.blockedDates')}</label>
          <div className="avail-chips">
            {config.blockedDates.map(date => (
              <span key={date} className="avail-chip">
                {date} <button onClick={() => removeBlockedDate(date)}>&times;</button>
              </span>
            ))}
          </div>
          <div className="avail-add-row">
            <input type="date" value={newBlockedDate} onChange={e => setNewBlockedDate(e.target.value)} />
            <button className="btn-small" onClick={addBlockedDate}>{t('integrations.calendar.availability.addDate')}</button>
          </div>
        </div>

        <div className="avail-subsection">
          <label>{t('integrations.calendar.availability.blockedRanges')}</label>
          <div className="avail-chips">
            {config.blockedRanges.map((range, i) => (
              <span key={i} className="avail-chip">
                {range.start} — {range.end}{range.label ? ` (${range.label})` : ''} <button onClick={() => removeBlockedRange(i)}>&times;</button>
              </span>
            ))}
          </div>
          <div className="avail-add-row">
            <input type="date" value={newRangeStart} onChange={e => setNewRangeStart(e.target.value)} placeholder={t('integrations.calendar.availability.rangeStart')} />
            <input type="date" value={newRangeEnd} onChange={e => setNewRangeEnd(e.target.value)} placeholder={t('integrations.calendar.availability.rangeEnd')} />
            <input type="text" value={newRangeLabel} onChange={e => setNewRangeLabel(e.target.value)} placeholder={t('integrations.calendar.availability.rangeLabel')} />
            <button className="btn-small" onClick={addBlockedRange}>{t('integrations.calendar.availability.addRange')}</button>
          </div>
        </div>
      </div>

      {/* Save */}
      <button className="btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? t('integrations.calendar.meeting.saving') : t('integrations.calendar.meeting.saveConfig')}
      </button>

      <style>{`
        .availability-config {
          margin-top: 1.5rem;
          border-top: 1px solid #e2e8f0;
          padding-top: 1.5rem;
        }
        .avail-header h4 { margin: 0 0 0.25rem; font-size: 1.1rem; }
        .avail-desc { color: #64748b; font-size: 0.875rem; margin: 0 0 1rem; }
        .avail-section { margin-bottom: 1.25rem; }
        .avail-section h5 { margin: 0 0 0.5rem; font-size: 0.95rem; color: #334155; }
        .avail-row { display: flex; gap: 1rem; flex-wrap: wrap; }
        .avail-field { flex: 1; min-width: 140px; }
        .avail-field label { display: block; font-size: 0.8rem; color: #64748b; margin-bottom: 0.25rem; }
        .avail-field select, .avail-field input {
          width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 6px;
          font-size: 0.875rem; background: white;
        }
        .avail-subsection { margin-bottom: 0.75rem; }
        .avail-subsection > label { font-size: 0.8rem; color: #64748b; display: block; margin-bottom: 0.25rem; }
        .avail-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-bottom: 0.5rem; }
        .avail-chip {
          display: inline-flex; align-items: center; gap: 0.25rem;
          background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 16px;
          padding: 0.2rem 0.6rem; font-size: 0.8rem;
        }
        .avail-chip button { background: none; border: none; cursor: pointer; font-size: 1rem; color: #94a3b8; padding: 0; line-height: 1; }
        .avail-add-row { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
        .avail-add-row input { padding: 0.4rem; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.8rem; }
        .avail-add-row input[type="text"] { flex: 1; min-width: 100px; }
        .btn-small { padding: 0.35rem 0.75rem; border: 1px solid #3b82f6; background: white; color: #3b82f6; border-radius: 6px; cursor: pointer; font-size: 0.8rem; white-space: nowrap; }
        .btn-small:hover { background: #eff6ff; }
        .btn-primary { width: 100%; padding: 0.6rem; background: #3b82f6; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 0.9rem; margin-top: 0.5rem; }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-primary:hover:not(:disabled) { background: #2563eb; }
      `}</style>
    </div>
  );
}
